import React from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import { Path } from 'app/const/Path';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DataTable } from 'app/components/Charts/table';
import { useStoreActions, useStoreState } from 'app/state/store/hooks';
import { downloadActivitiesCSV } from 'app/utils/downloadActivitiesCSV';
import { LinkCell } from 'app/components/Charts/table/common/cells/LinkCell';
import {
  selectedFilterAtom,
  transactionTypeAtom,
} from 'app/state/recoil/atoms';
import {
  getAPIFormattedFilters,
  AF_REPORTING_ORG_REF,
} from 'app/utils/getAPIFormattedFilters';
import {
  PublishersDataTableColumns,
  PublishersDataTableOptions,
} from 'app/components/Charts/table/data';

interface PublishersFragmentTableProps {
  pageParam: string;
  detailBaseFilter: string;
}

export function PublishersFragmentTable(props: PublishersFragmentTableProps) {
  const [selectedFilters, _] = useRecoilState(selectedFilterAtom);
  const [tablePage, setTablePage] = React.useState(0);
  const [tableRows, setTableRows] = React.useState(10);
  const transactionType = useRecoilValue(transactionTypeAtom);
  const [tableSortBy, setTableSortBy] = React.useState<{
    name: string;
    direction: 'asc' | 'desc';
  }>({
    name: transactionType.name,
    direction: 'desc',
  });
  const action = useStoreActions(
    (actions) => actions.detailPublishersTable.fetch
  );
  const actionData = useStoreState(
    (actions) => actions.detailPublishersTable.data
  );

  const tableData = get(actionData, 'data', []);
  const tableCount = get(actionData, 'count', 0);

  function callAction(page: number, rows: number, sort: string) {
    const filters = getAPIFormattedFilters(selectedFilters);
    action({
      values: {
        filters: {
          ...filters,
          [props.detailBaseFilter]: [props.pageParam],
        },
        page,
        rows,
        sort,
      },
    });
  }

  React.useEffect(() => {
    callAction(
      tablePage,
      tableRows,
      `${tableSortBy.name} ${tableSortBy.direction}`
    );
  }, [props.pageParam, tablePage, tableRows, tableSortBy, selectedFilters]);

  const publisherComp = (value: any) => {
    const label = value[1] === '' ? value[0] : value[1];
    const link = `${Path.detail.publisher.replace(
      ':param',
      encodeURIComponent(value[0])
    )}/projects?${props.detailBaseFilter}=${props.pageParam}`;
    return <LinkCell link={link} value={label} />;
  };

  PublishersDataTableColumns(transactionType.name)[1] = {
    name: 'Publisher',
    options: {
      customBodyRender: publisherComp,
    },
  };

  return (
    <DataTable
      data={tableData}
      columns={PublishersDataTableColumns(transactionType.name)}
      options={{
        ...PublishersDataTableOptions,
        serverSide: true,
        sortOrder: tableSortBy,
        count: tableCount,
        onDownload: () => {
          let filters = getAPIFormattedFilters(selectedFilters);
          if (
            !find(
              Object.keys(filters),
              (key: string) => key === AF_REPORTING_ORG_REF
            )
          ) {
            filters = {
              ...filters,
              [AF_REPORTING_ORG_REF]: ['*'],
            };
          }
          downloadActivitiesCSV(
            {
              ...filters,
              [props.detailBaseFilter]: [props.pageParam],
            },
            '',
            '',
            20000,
            props.pageParam
          );
          return false;
        },
        onChangePage: setTablePage,
        onChangeRowsPerPage: setTableRows,
        onColumnSortChange: (
          changedColumn: string,
          direction: 'asc' | 'desc'
        ) =>
          setTableSortBy({
            name: changedColumn,
            direction,
          }),
      }}
      title={`AIDA has found ${new Intl.NumberFormat('nl-NL').format(
        tableCount
      )} publishers for you`}
    />
  );
}
